import {
  DeleteParamWidgetsDocument,
  RemoveTextWidgetsMutation,
  RemoveTextWidgetsMutationVariables,
  RemoveVisualizationWidgetsMutation,
  RemoveVisualizationWidgetsMutationVariables,
  Text_Widgets_Constraint,
  Text_Widgets_Update_Column,
  UpsertTextWidgetMutation,
  UpsertTextWidgetMutationVariables,
} from "src/gql/codegen/graphql";
import { Session } from "src/utils/users/types";
import { FullDashboard } from "src/gql/entries/types";
import { gql } from "@apollo/client";
import { createWidgetOptions } from "src/utils/widgets/widgets";
import { isQueryVisual } from "src/gql/visuals/visuals";
import { apolloCore } from "src/utils/apollo/apollo";
import AmplitudeService from "src/services/amplitude";

export const callRemoveTextWidget = (widgetId: number, session: Session) => {
  return apolloCore.mutate<
    RemoveTextWidgetsMutation,
    RemoveTextWidgetsMutationVariables
  >({
    mutation: removeTextWidgets,
    context: { session },
    fetchPolicy: "no-cache",
    variables: { widgets: [widgetId] },
  });
};

export const callRemoveVisualizationWidget = (
  widgetId: number,
  session: Session
) => {
  return apolloCore.mutate<
    RemoveVisualizationWidgetsMutation,
    RemoveVisualizationWidgetsMutationVariables
  >({
    mutation: removeVisualizationWidgets,
    context: { session },
    fetchPolicy: "no-cache",
    variables: { widgets: [widgetId] },
  });
};

export const callUpsertTextWidget = (
  dashboard: FullDashboard,
  widgetId: number | undefined,
  text: string,
  session: Session
) => {
  if (!text) {
    throw new Error("missing widget text");
  }

  return apolloCore.mutate<
    UpsertTextWidgetMutation,
    UpsertTextWidgetMutationVariables
  >({
    mutation: upsertTextWidget,
    context: { session },
    fetchPolicy: "no-cache",
    variables: {
      object: {
        dashboard_id: dashboard.id,
        options: createWidgetOptions(),
        id: widgetId,
        text,
      },
      on_conflict: {
        constraint: Text_Widgets_Constraint.TextWidgetsPkey,
        update_columns: [Text_Widgets_Update_Column.Text],
      },
    },
  });
};

// Each each param_widget should always have an associated query.
// Here we're checking if that query still has a param with that key.
// So if there are param_widgets that we no longer can find in the queries params,
// we delete them from the DB.
export const removeOrphanParams = async (
  dashboard: Partial<FullDashboard>,
  session: Session
) => {
  const paramsToDelete: string[] = [];
  dashboard.param_widgets?.forEach((param) => {
    const viz = dashboard.visualization_widgets?.find(
      (vw) => vw.id === param.visualization_widget_id
    )?.visualization;

    if (isQueryVisual(viz) && viz.query_details) {
      if (!viz.query_details.parameters.find((p) => p.key === param.key)) {
        paramsToDelete.push(param.id);
      }
    }
  });

  if (paramsToDelete.length === 0) {
    return;
  }

  const idsToDelete = paramsToDelete.map((id) => ({
    id: {
      _eq: id,
    },
  }));

  await apolloCore.mutate({
    context: { session },
    mutation: DeleteParamWidgetsDocument,
    variables: {
      ids: idsToDelete,
    },
  });

  if (dashboard.id !== undefined) {
    AmplitudeService.trackEvent("Dashboard Widget Removed", {
      dashboard_id: dashboard.id,
    });
  }
};

gql`
  mutation DeleteParamWidgets($ids: [param_widgets_bool_exp!]!) {
    delete_param_widgets(where: { _or: $ids }) {
      affected_rows
    }
  }
`;

export const insertVisualizationWidget = gql`
  mutation InsertVisualizationWidget(
    $object: visualization_widgets_insert_input!
  ) {
    insert_visualization_widgets_one(object: $object) {
      id
    }
  }
`;

export const removeVisualizationWidgets = gql`
  mutation RemoveVisualizationWidgets($widgets: [Int!]!) {
    delete_visualization_widgets(where: { id: { _in: $widgets } }) {
      returning {
        id
      }
    }
  }
`;

const upsertTextWidget = gql`
  mutation UpsertTextWidget(
    $object: text_widgets_insert_input!
    $on_conflict: text_widgets_on_conflict!
  ) {
    insert_text_widgets_one(object: $object, on_conflict: $on_conflict) {
      id
    }
  }
`;

const removeTextWidgets = gql`
  mutation RemoveTextWidgets($widgets: [Int!]!) {
    delete_text_widgets(where: { id: { _in: $widgets } }) {
      returning {
        id
      }
    }
  }
`;
